// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import type { TyLang, TyLangValue } from "@/types";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    label: string;
    value: TyLangValue[];
    onChange: (value: TyLangValue[]) => void;
    placeholder?: string;
    /** For text where the line breaks a company types are the formatting — a description, a note. */
    multiline?: boolean;
    rows?: number;
    disabled?: boolean;
};

/**
 * One tenant-authored field, in every language at once, inside a single bordered card.
 *
 * Every string a company writes lands on the public catalog, so it is stored as `[{lang, value}]` and
 * has to be edited as such. One card rather than two loose fields is what keeps a form from reading as
 * twice as long as it is: the label is said once, and the languages are two boxes under it, each
 * tagged on its own edge.
 *
 * Filling both is not required and the UI does not nag about it — every read falls back across
 * languages, so an Arabic-only company has a complete record, not a half-finished one. Blank entries
 * are dropped rather than stored as empty strings.
 *
 * The row does not follow the page's direction. Arabic is always the box on the right and English
 * always the box on the left, because that is the side each one's text starts from — mirroring them
 * with the interface would put a right-to-left field on the left of a left-to-right one.
 */
export default function MultiLangInput({ label, value, onChange, placeholder, multiline, rows = 3, disabled }: Props) {
    const read = (lang: TyLang) => value.find((entry) => entry.lang === lang)?.value ?? "";
    const write = (lang: TyLang, next: string) => {
        const rest = value.filter((entry) => entry.lang !== lang);
        onChange(next.trim() ? [...rest, { lang, value: next }] : rest);
    };

    return (
        <div className="lang-input">
            <span className="lang-input__label">{label}</span>

            <div className="lang-input__row">
                {constants.languages.map((lang) => {
                    const isRtl = constants.rtlLanguages.includes(lang as "ar");
                    const shared = {
                        className: `lang-input__field${multiline ? " lang-input__field--area" : ""}`,
                        value: read(lang),
                        dir: (isRtl ? "rtl" : "ltr") as "rtl" | "ltr",
                        placeholder,
                        disabled,
                    };

                    return (
                        // The box carries its own direction, so the tag below lands on the side that
                        // language reads from — the right for Arabic, the left for English.
                        <div key={lang} className="lang-input__wrap" dir={shared.dir}>
                            {/* On the field's own edge, so two languages stay tellable apart without
                                costing a second row of labels. */}
                            <span className="lang-input__tag">{constants.languageNames[lang]}</span>
                            {multiline ? (
                                <textarea {...shared} rows={rows} onChange={(event) => write(lang, event.target.value)} />
                            ) : (
                                <input {...shared} type="text" onChange={(event) => write(lang, event.target.value)} />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
